/* theme.ts */
import { extendTheme, defineStyle, defineStyleConfig } from "@chakra-ui/react";
import avatarTheme from "./anatomy-theme";
import modalTheme from "./modal-theme";

const config = {
  initialColorMode: "light",
  useSystemColorMode: false,
};

export const colors = {
  amber: {
    400: "#ffbf00",
  },
  black: {
    50: "#363637",
    100: "#000000",
    200: "#1D1D1F",
    300: "#262628",
  },
  blue: "#007AFF",
  gray: {
    50: "#EFF1F6",
    100: "#E6E9EF",
    400: "#56616B",
    500: "#31373D",
    600: "#D9D9D9",
  },
  lime: {
    100: "#DFFFB2",
  },
  orange: {
    500: "#E55F00",
  },
  primary: "#131316",
  red: {
    100: "#FFCCDB",
    400: "#ff0049",
  },
  transparent: "transparent",
  white: {
    50: "#FAFAFA",
    100: "#ffffff",
  },
  polychromeAmberRed: "linear-gradient(137deg, #FFBF00 0%, #FF0049 96.86%)",
};

const fonts = {
  heading: "Bricolage Grotesque, sans-serif",
  body: "Bricolage Grotesque, sans-serif",
  degular: "var(--font-secondary)",
  bricolage: "Bricolage Grotesque, sans-serif",
};

const fontSizes = {
  xs: "0.75rem",
  sm: "0.875rem",
  md: "1rem",
  lg: "1.125rem",
  xl: "1.25rem",
  "2xl": "1.5rem",
  "3xl": "1.875rem",
  "4xl": "2rem",
  "5xl": "3rem",
  "6xl": "3.75rem",
  "7xl": "4.5rem",
  "8xl": "6rem",
  "9xl": "8rem",
};

const fontWeights = {
  hairline: 100,
  thin: 200,
  light: 300,
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
  black: 900,
};

const lineHeights = {
  normal: "normal",
  none: 1,
  shorter: 1.25,
  short: 1.375,
  base: 1.5,
  tall: 1.625,
  taller: "2",
  3: ".75rem",
  4: "1rem",
  5: "1.25rem",
  6: "1.5rem",
  7: "1.75rem",
  8: "2rem",
  9: "2.25rem",
  10: "2.5rem",
};

const letterSpacings = {
  tighter: "-0.05em",
  tight: "-0.025em",
  normal: "0",
  wide: "0.025em",
  wider: "0.05em",
  widest: "0.1em",
};

const radii = {
  none: "0",
  sm: "0.125rem",
  base: "0.25rem",
  md: "0.375rem",
  lg: "0.5rem",
  xl: "0.75rem",
  "2xl": "1rem",
  "3xl": "1.125rem",
  "4xl": "1.5rem",
  full: "9999px",
};

const sizes = {
  xxxl: defineStyle({
    maxW: "1440px",
    px: "3",
  }),
  xxl: defineStyle({
    maxW: "1192px",
    px: "4",
  }),
  fullW: defineStyle({
    w: "100vw",
    maxW: "100%",
  }),
  fullH: defineStyle({
    minH: "100vh",
    maxH: "100%",
  }),
};

const containerTheme = defineStyleConfig({ sizes });

const styles = {
  global: {
    body: {
      bg: "var(--background-rgb)",
      color: "var(--foreground-rgb)",
      fontFamily: "Bricolage Grotesque, sans-serif",
    },
    button: {
      fontFamily: "Bricolage Grotesque, sans-serif",
    },
    html: {
      fontFamily: "Bricolage Grotesque, sans-serif",
    },
    "*": {
      fontFamily: "Bricolage Grotesque, sans-serif",
    },
  },
};

export const theme = extendTheme({
  colors,
  components: {
    Avatar: avatarTheme,
    Container: containerTheme,
    Modal: modalTheme,
    Drawer: modalTheme,
  },
  config,
  fonts,
  fontSizes,
  fontWeights,
  letterSpacings,
  lineHeights,
  radii,
  sizes,
  styles,
});
